import React from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Header, Footer, SEO } from '../../../shared/components';

const APP_NAME = import.meta.env.VITE_APP_NAME === 'plotmagic' ? 'PlotMagic' : 'RRD Planners';
const LEGAL_NAME = 'Kuthati Sai Ram';
const ADDRESS = 'HNO 33-5-202, Satyasai Nagar, Opp Naidu Pump, Warangal, Telangana - 506002';
const EMAIL = 'rrdplotmagic@gmail.com';
const PHONE = '+91 9573766436';

const PrivacyPage: React.FC = () => {
  return (
    <PageLayout size="full">
      <SEO title={`Privacy Policy - ${APP_NAME}`} description="How we collect, use, and protect your personal information" />
      <Header />
      <PageLayout.Content>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-8">
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold text-foreground">Privacy Policy</h1>
              <p className="text-muted-foreground">Last updated: April 2026</p>
            </div>

            <div className="prose prose-gray dark:prose-invert max-w-none space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">1. Information We Collect</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">We collect the following personal information when you use {APP_NAME}:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li><strong>Account Information:</strong> Name, email address, phone number, password (encrypted)</li>
                  <li><strong>Payment Information:</strong> Transaction details processed securely via our payment gateway. We do not store your card/UPI details on our servers.</li>
                  <li><strong>Usage Data:</strong> Plot designs created, download history, credit balance and transactions</li>
                  <li><strong>Device Information:</strong> Browser type, IP address, device type, operating system for security and analytics purposes</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">2. How We Use Your Information</h2>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>To provide and maintain our plot map generation services</li>
                  <li>To process credit purchases and manage your account balance</li>
                  <li>To send transaction confirmations and service-related notifications</li>
                  <li>To improve our platform, features, and user experience</li>
                  <li>To prevent fraud and ensure platform security</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">3. Information Sharing</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">We do not sell your personal information. We may share your data with:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li><strong>Payment Processors:</strong> Our payment gateway, for processing credit pack purchases securely</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or government authority</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">4. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement industry-standard security measures including encrypted passwords (bcrypt hashing), JWT-based authentication, HTTPS encryption for all data in transit, rate limiting on sensitive endpoints, and role-based access control. However, no method of transmission over the internet is 100% secure.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">5. Data Retention</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We retain your personal information for as long as your account is active or as needed to provide our services. Plot design data is retained until you delete it. Transaction records are retained as required by applicable tax and accounting laws.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">6. Your Rights</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">You have the right to:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Access your personal data stored on our platform</li>
                  <li>Correct inaccurate personal information</li>
                  <li>Request deletion of your account and personal data</li>
                  <li>Download your plot design data</li>
                  <li>Withdraw consent for non-essential data processing</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">To exercise these rights, contact us at <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a>.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">7. Cookies & Tracking</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We use browser localStorage to persist your session (JWT token) and plot form data for convenience. We do not use third-party tracking cookies or advertising trackers. You can clear your browser storage at any time.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">8. Children's Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">9. Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date. Continued use of the Platform after changes constitutes acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">10. Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  For any privacy-related concerns or requests:<br /><br />
                  <strong>{LEGAL_NAME}</strong><br />
                  {ADDRESS}<br />
                  Email: <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a><br />
                  Phone: <a href={`tel:${PHONE}`} className="text-primary hover:underline">{PHONE}</a>
                </p>
              </section>
            </div>

            <div className="text-center pt-8 border-t border-border">
              <a href="/" className="inline-flex items-center text-primary hover:underline font-medium">← Back to Home</a>
            </div>
          </div>
        </div>
      </PageLayout.Content>
      <Footer />
    </PageLayout>
  );
};

export default PrivacyPage;
